import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useParams, Link } from 'react-router-dom';
import { comics, categories } from '@/mocks/comics';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const comic = comics.find(c => c.slug === slug);

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-16 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <i className="ri-emotion-sad-line text-4xl text-foreground-300"></i>
            </div>
            <h2 className="text-xl font-heading font-bold text-foreground-900 mb-2">作品未找到</h2>
            <p className="text-foreground-500 mb-6">该漫画作品不存在或已下架</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const category = categories.find(c => c.slug === comic.category);
  const relatedComics = comics.filter(c => c.category === comic.category && c.id !== comic.id).slice(0, 4);

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200/60">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6 py-6">
            <nav className="flex items-center gap-2 text-sm mb-4 flex-wrap">
              <Link to="/" className="text-foreground-500 hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
              <span className="text-foreground-400">/</span>
              {category && (
                <>
                  <Link to={`/category/${category.slug}`} className="text-foreground-500 hover:text-primary-600 transition-colors cursor-pointer">{category.name}</Link>
                  <span className="text-foreground-400">/</span>
                </>
              )}
              <span className="text-foreground-800 font-medium">{comic.title}</span>
            </nav>
          </div>
        </div>

        <div className="w-full max-w-7xl mx-auto px-4 md:px-6 py-8">
          <div className="flex flex-col lg:flex-row gap-8">
            <div className="flex-shrink-0 w-full sm:w-64">
              <div className="sticky top-20">
                <div className="rounded-lg overflow-hidden">
                  <img
                    src={comic.cover}
                    alt={`${comic.title} - 妖精漫画免费阅读韩漫`}
                    title={`${comic.title} - 妖精漫画免费阅读`}
                    className="w-full aspect-[3/4] object-cover object-top"
                  />
                </div>
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                {comic.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium">
                    {tag}
                  </span>
                ))}
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  comic.status === '连载中'
                    ? 'bg-green-100 text-green-700'
                    : 'bg-foreground-100 text-foreground-600'
                }`}>
                  {comic.status}
                </span>
              </div>

              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
                {comic.title}
              </h1>
              <p className="text-sm text-foreground-500 mb-4">作者：{comic.author}</p>

              <div className="flex flex-wrap items-center gap-4 md:gap-6 mb-6 p-4 rounded-lg bg-background-100">
                <div className="flex items-center gap-1.5">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-star-fill text-lg" style={{ color: `oklch(var(--accent-500))` }}></i>
                  </div>
                  <span className="text-sm font-semibold text-foreground-900">{comic.rating}</span>
                  <span className="text-xs text-foreground-500">评分</span>
                </div>
                <div className="w-px h-6 bg-background-300"></div>
                <div className="flex items-center gap-1.5">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-eye-line text-lg text-foreground-500"></i>
                  </div>
                  <span className="text-sm font-semibold text-foreground-900">{comic.views >= 10000 ? `${(comic.views / 10000).toFixed(1)}万` : comic.views}</span>
                  <span className="text-xs text-foreground-500">阅读</span>
                </div>
                <div className="w-px h-6 bg-background-300"></div>
                <div className="flex items-center gap-1.5">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-file-list-3-line text-lg text-foreground-500"></i>
                  </div>
                  <span className="text-sm font-semibold text-foreground-900">{comic.chapters}</span>
                  <span className="text-xs text-foreground-500">话</span>
                </div>
              </div>

              <div className="mb-8">
                <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">作品简介</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
              </div>

              <div className="mb-2">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-heading text-base font-semibold text-foreground-900">章节列表</h3>
                  <div className="flex items-center gap-2">
                    <button className="px-3 py-1.5 rounded-lg text-xs font-medium bg-background-100 text-foreground-500 hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer">
                      正序
                    </button>
                  </div>
                </div>
                <div className="border border-background-200/60 rounded-lg overflow-hidden">
                  {comic.chapters_list.map((ch, idx) => (
                    <div
                      key={ch.num}
                      className={`flex items-center justify-between px-4 py-3 transition-colors hover:bg-background-100 cursor-pointer ${
                        idx < comic.chapters_list.length - 1 ? 'border-b border-background-200/60' : ''
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-medium text-foreground-900 whitespace-nowrap">
                          第{ch.num}话
                        </span>
                        <span className="text-sm text-foreground-600">{ch.title}</span>
                        {idx === 0 && (
                          <span className="px-1.5 py-0.5 rounded text-xs bg-accent-100 text-accent-700">NEW</span>
                        )}
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-foreground-400">{ch.date}</span>
                        <div className="w-5 h-5 flex items-center justify-center">
                          <i className="ri-arrow-right-s-line text-foreground-400"></i>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="text-center mt-6">
                <button className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg bg-background-100 text-foreground-600 text-sm font-medium hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer">
                  查看更多章节
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-down-s-line text-sm"></i>
                  </div>
                </button>
              </div>
            </div>
          </div>

          {relatedComics.length > 0 && (
            <div className="mt-14 pt-10 border-t border-background-200/60">
              <h2 className="font-heading text-xl font-bold text-foreground-900 mb-6">
                相关推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5">
                {relatedComics.map((rc) => (
                  <Link
                    key={rc.id}
                    to={`/article/${rc.slug}`}
                    className="group cursor-pointer"
                    data-product-shop
                  >
                    <div className="relative overflow-hidden rounded-lg mb-3">
                      <img
                        src={rc.cover}
                        alt={`${rc.title} - 妖精漫画免费阅读`}
                        title={`${rc.title} - 妖精漫画免费韩漫`}
                        className="w-full aspect-[3/4] object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <h3 className="font-heading text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">
                      {rc.title}
                    </h3>
                    <div className="flex items-center gap-1 mt-1">
                      <div className="w-3.5 h-3.5 flex items-center justify-center">
                        <i className="ri-star-fill text-xs" style={{ color: `oklch(var(--accent-500))` }}></i>
                      </div>
                      <span className="text-xs text-foreground-500">{rc.rating}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}